import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mockInfluencers, mockCommissionTiers, mockMyEarnings } from "@/mocks/influencers";
import SEO from "@/components/base/SEO";

type Tab = "overview" | "earnings" | "apply";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const influencerSchema = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Reseller & Star Portal — Earn Commission | StarBookPrime",
    "description": "TikTok, YouTube, Facebook, Instagram, Twitter, Douyin, WeChat stars — promote verified factory products, earn commission on every sale, get paid via PayPal.",
    "url": `${SITE_URL}/influencer`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Reseller & Star Portal", "item": `${SITE_URL}/influencer` }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "StarBookPrime Reseller & Influencer Commission Program",
    "description": "Earn up to 20% commission by promoting verified factory products on TikTok, YouTube, Instagram, and more. Free to join, paid via PayPal.",
    "provider": {
      "@type": "Organization",
      "name": "StarBookPrime"
    },
    "serviceType": "Affiliate Marketing",
    "areaServed": "Worldwide",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free to join — earn 5–20% commission per sale"
    }
  }
];

const platformIcon: Record<string, string> = {
  tiktok: "ri-tiktok-line",
  youtube: "ri-youtube-line",
  twitter: "ri-twitter-x-line",
  instagram: "ri-instagram-line",
  facebook: "ri-facebook-fill",
  douyin: "ri-tiktok-line",
  weixin: "ri-wechat-line",
};

const platformLabel: Record<string, string> = {
  tiktok: "TikTok",
  youtube: "YouTube",
  twitter: "Twitter/X",
  instagram: "Instagram",
  facebook: "Facebook",
  douyin: "Douyin",
  weixin: "WeChat",
};

const platformColor: Record<string, string> = {
  tiktok: "bg-gray-900 text-white",
  youtube: "bg-red-500 text-white",
  twitter: "bg-gray-800 text-white",
  instagram: "bg-rose-500 text-white",
  facebook: "bg-blue-600 text-white",
  douyin: "bg-black text-white",
  weixin: "bg-green-500 text-white",
};

export default function InfluencerPage() {
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [applied, setApplied] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState("jessica@paypal.com");
  const [paypalSaved, setPaypalSaved] = useState(false);
  const [withdrawRequested, setWithdrawRequested] = useState(false);
  const [filterPlatform, setFilterPlatform] = useState("all");

  const totalEarned = mockMyEarnings.reduce((s, e) => s + e.commission, 0);
  const paidOut = mockMyEarnings.filter((e) => e.status === "paid").reduce((s, e) => s + e.commission, 0);
  const pending = mockMyEarnings.filter((e) => e.status === "pending").reduce((s, e) => s + e.commission, 0);
  const totalSales = mockMyEarnings.length;

  const handleSavePaypal = () => {
    setPaypalSaved(true);
    setTimeout(() => setPaypalSaved(false), 2500);
  };

  const handleWithdraw = () => {
    setWithdrawRequested(true);
    setTimeout(() => setWithdrawRequested(false), 3000);
  };

  const tabs: { value: Tab; label: string; icon: string }[] = [
    { value: "overview", label: "Overview", icon: "ri-bar-chart-line" },
    { value: "earnings", label: "My Earnings", icon: "ri-money-dollar-circle-line" },
    { value: "apply", label: "Apply to Join", icon: "ri-user-add-line" },
  ];

  return (
    <div className="min-h-screen" style={{backgroundColor: "#f5f5f5"}}>
      <SEO
        title="Reseller & Star Portal — Earn Commission via PayPal | StarBookPrime"
        description="Join free as a reseller or influencer. Promote verified factory products on TikTok, YouTube, Instagram, and more. Earn up to 15% commission paid via PayPal. 12,400+ active resellers."
        keywords="reseller commission program, influencer affiliate marketing, earn PayPal commission"
        canonical="/influencer"
        ogImage="https://public.readdy.ai/ai/img_res/269fef46b7f33679b81008d9dcd01562.jpg"
        schema={influencerSchema}
      />
      <Navbar />
      <div className="pt-16">
        {/* Hero */}
        <div className="relative py-20 px-6 md:px-12 overflow-hidden bg-gray-900">
          <div className="absolute inset-0 opacity-20">
            <img src="https://readdy.ai/api/search-image?query=abstract%20orange%20gold%20network%20lines%20connections%20dark%20background%20influencer%20social%20media%20marketing%20global%20platforms&width=1440&height=500&seq=infhero2&orientation=landscape" alt="" className="w-full h-full object-cover object-top" />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <span className="inline-block bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-5">Reseller &amp; Star Portal</span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
              Promote. Sell. Earn via <span className="text-orange-400">PayPal</span>
            </h1>
            <p className="text-gray-300 text-lg mb-6 max-w-2xl mx-auto">
              TikTok, YouTube, Facebook, Instagram, Twitter, Douyin, WeChat stars — promote verified factory products, earn commission on every sale, get paid via PayPal.
            </p>
            {/* Platform badges */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {Object.entries(platformLabel).map(([key, label]) => (
                <span key={key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${platformColor[key]}`}>
                  <i className={platformIcon[key]} />{label}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <button onClick={() => setActiveTab("apply")} className="px-8 py-3.5 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                Join Free — No Monthly Fee
              </button>
              <button onClick={() => setActiveTab("earnings")} className="px-8 py-3.5 border border-white/30 text-white font-bold rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap">
                View My Earnings
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          {/* Tabs */}
          <div className="flex gap-1 mb-8 bg-white border border-gray-100 rounded-xl p-1 w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab.value ? "bg-orange-500 text-white" : "text-gray-500 hover:text-gray-700"}`}
              >
                <i className={tab.icon} />{tab.label}
              </button>
            ))}
          </div>

          {/* OVERVIEW TAB */}
          {activeTab === "overview" && (
            <>
              {/* Key stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                {[
                  { label: "Active Resellers", value: "12,400+", icon: "ri-user-star-line", color: "text-orange-500 bg-orange-50" },
                  { label: "Total Commission Paid", value: "$2.4M+", icon: "ri-paypal-line", color: "text-blue-600 bg-blue-50" },
                  { label: "Verified Products", value: "8,500+", icon: "ri-shield-check-line", color: "text-green-600 bg-green-50" },
                  { label: "Platforms Supported", value: "7+", icon: "ri-global-line", color: "text-rose-500 bg-rose-50" },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-5">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 ${s.color}`}>
                      <i className={`${s.icon} text-lg`} />
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{s.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Commission Tiers */}
              <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Commission Tiers</h2>
                <p className="text-sm text-gray-500 mb-6">Each factory sets their own commission %. Below are the platform-wide tiers based on your follower count. Higher tiers unlock better rates.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {mockCommissionTiers.map((tier, i) => (
                    <div key={tier.tier} className={`rounded-xl p-6 border ${i === 3 ? "bg-gray-900 border-orange-500 text-white" : "bg-white border-gray-100"}`}>
                      <div className={`text-xs font-bold uppercase tracking-widest mb-2 ${i === 3 ? "text-orange-400" : "text-gray-400"}`}>{tier.tier}</div>
                      <div className={`text-3xl font-extrabold mb-1 ${i === 3 ? "text-orange-400" : "text-orange-500"}`}>{tier.commission}</div>
                      <div className={`text-xs mb-4 ${i === 3 ? "text-gray-400" : "text-gray-500"}`}>{tier.followers} followers</div>
                      <ul className="space-y-2">
                        {tier.perks.map((perk) => (
                          <li key={perk} className={`flex items-center gap-2 text-xs ${i === 3 ? "text-gray-300" : "text-gray-600"}`}>
                            <i className="ri-check-line text-orange-500 flex-shrink-0" />{perk}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Earners */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Top Earning Stars &amp; Resellers</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {mockInfluencers.map((inf) => (
                    <div key={inf.id} className="bg-white rounded-xl border border-gray-100 p-5 hover:border-orange-200 transition-colors">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="relative w-14 h-14 flex-shrink-0">
                          <img src={inf.avatar} alt={inf.name} className="w-14 h-14 rounded-full object-cover object-top" />
                          {inf.verified && (
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 flex items-center justify-center bg-orange-500 rounded-full">
                              <i className="ri-check-line text-white text-xs" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-gray-900 text-sm">{inf.name}</div>
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${platformColor[inf.platform]}`}>
                              <i className={platformIcon[inf.platform]} />
                              {platformLabel[inf.platform]}
                            </span>
                            <span>{inf.followers}</span>
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">{inf.category}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-orange-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-orange-600">{inf.earnings}</div>
                          <div className="text-xs text-gray-400">Earned</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-green-600">{inf.sales.toLocaleString()}</div>
                          <div className="text-xs text-gray-400">Sales</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2">
                          <div className="text-sm font-bold text-gray-700">{inf.commission}</div>
                          <div className="text-xs text-gray-400">Rate</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* EARNINGS TAB */}
          {activeTab === "earnings" && (
            <div>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {[
                  { label: "Total Earned", value: `$${totalEarned.toFixed(2)}`, icon: "ri-money-dollar-circle-line", color: "text-green-600 bg-green-50" },
                  { label: "Paid Out", value: `$${paidOut.toFixed(2)}`, icon: "ri-paypal-line", color: "text-blue-600 bg-blue-50" },
                  { label: "Pending", value: `$${pending.toFixed(2)}`, icon: "ri-time-line", color: "text-yellow-600 bg-yellow-50" },
                  { label: "Total Sales", value: `${totalSales}`, icon: "ri-shopping-bag-line", color: "text-orange-500 bg-orange-50" },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-5">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 ${s.color}`}>
                      <i className={`${s.icon} text-lg`} />
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">{s.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* PayPal Payout Section */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-8">
                <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-paypal-line text-blue-600 text-xl" />
                  PayPal Payout Settings
                </h3>
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1.5">Your PayPal Email</label>
                    <div className="relative">
                      <i className="ri-paypal-line absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-sm" />
                      <input
                        type="email"
                        value={paypalEmail}
                        onChange={(e) => setPaypalEmail(e.target.value)}
                        placeholder="your@paypal.com"
                        className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-400 transition-colors"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleSavePaypal}
                    className="sm:self-end px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {paypalSaved ? <span><i className="ri-checkbox-circle-fill mr-1" />Saved!</span> : "Save PayPal"}
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Available to Withdraw</p>
                    <p className="text-2xl font-extrabold text-blue-600">${paidOut.toFixed(2)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Minimum payout: $10 · Pending commissions paid after order delivery</p>
                  </div>
                  <button
                    onClick={handleWithdraw}
                    disabled={paidOut < 10}
                    className="px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 text-sm"
                  >
                    {withdrawRequested ? (
                      <span><i className="ri-checkbox-circle-fill mr-2" />Request Sent!</span>
                    ) : (
                      <span><i className="ri-paypal-line mr-2" />Withdraw via PayPal</span>
                    )}
                  </button>
                </div>
              </div>

              {/* Filter by platform */}
              <div className="flex flex-wrap gap-2 mb-5">
                <button
                  onClick={() => setFilterPlatform("all")}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer ${filterPlatform === "all" ? "bg-orange-500 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"}`}
                >
                  All Platforms
                </button>
                {Object.entries(platformLabel).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setFilterPlatform(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer ${filterPlatform === key ? platformColor[key] : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300"}`}
                  >
                    <i className={platformIcon[key]} />{label}
                  </button>
                ))}
              </div>

              {/* Earnings Table */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">Product</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Factory</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Order Value</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Commission</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockMyEarnings.map((e) => (
                        <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3.5">
                            <p className="font-medium text-gray-900 text-xs line-clamp-1">{e.productTitle}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{e.factoryCommission} rate</p>
                          </td>
                          <td className="px-4 py-3.5 text-xs text-gray-600">{e.factory}</td>
                          <td className="px-4 py-3.5 text-xs text-gray-500">{e.orderDate}</td>
                          <td className="px-4 py-3.5 text-right text-xs font-medium text-gray-900">${e.amount.toFixed(2)}</td>
                          <td className="px-4 py-3.5 text-right text-sm font-bold text-green-600">+${e.commission.toFixed(2)}</td>
                          <td className="px-4 py-3.5 text-center">
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${e.status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                              {e.status === "paid" ? "Paid" : "Pending"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-orange-50 border-t border-orange-100">
                        <td colSpan={4} className="px-5 py-3 text-sm font-bold text-gray-900">Total</td>
                        <td className="px-4 py-3 text-right text-sm font-extrabold text-green-600">+${totalEarned.toFixed(2)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* APPLY TAB */}
          {activeTab === "apply" && (
            <div className="max-w-2xl mx-auto">
              {!applied ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-8">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 flex items-center justify-center bg-rose-50 rounded-xl">
                      <i className="ri-star-line text-rose-500 text-xl" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Join as Reseller / Star</h2>
                      <p className="text-xs text-gray-500">100% free · No monthly fee · Earn via PayPal</p>
                    </div>
                  </div>

                  <div className="my-5 p-4 bg-rose-50 rounded-xl border border-rose-100 text-sm text-rose-700">
                    <i className="ri-information-line mr-2" />
                    <strong>Important:</strong> You may only promote products from our verified factories. All commissions are paid via PayPal.
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                        <input placeholder="Your full name" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                        <input type="email" placeholder="your@email.com" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">PayPal Email (for commission payouts) *</label>
                      <div className="relative">
                        <i className="ri-paypal-line absolute left-3 top-1/2 -translate-y-1/2 text-blue-500 text-sm" />
                        <input type="email" placeholder="your@paypal.com" className="w-full pl-9 pr-4 border border-gray-200 rounded-lg py-2.5 text-sm outline-none focus:border-blue-400" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Primary Platform *</label>
                      <select className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400 cursor-pointer bg-white">
                        <option value="">Select platform</option>
                        {["TikTok", "YouTube", "Facebook", "Instagram", "Twitter/X", "Douyin", "WeChat / Weixin", "Other"].map((p) => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Profile / Channel URL *</label>
                      <input placeholder="https://tiktok.com/@yourhandle" className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Follower Count *</label>
                      <select className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400 cursor-pointer bg-white">
                        <option value="">Select range</option>
                        {["Under 1K", "1K - 10K", "10K - 100K", "100K - 500K", "500K - 1M", "1M+"].map((r) => <option key={r}>{r}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Content Niche *</label>
                      <input placeholder="e.g. Tech, Beauty, Fitness, Lifestyle..." className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Why do you want to join? (optional)</label>
                      <textarea rows={3} maxLength={500} placeholder="Tell us about yourself and your audience..." className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-orange-400 resize-none" />
                    </div>
                    <button onClick={() => setApplied(true)} className="w-full py-3.5 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-base">
                      Submit Application — It&apos;s Free
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-500 text-3xl mx-auto mb-4">
                    <i className="ri-checkbox-circle-line" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
                  <p className="text-gray-500 mb-2">Our team will review your profile within 24 hours.</p>
                  <p className="text-sm text-gray-400 mb-6">Once approved, you&apos;ll get access to all verified factory products and your unique referral links. Commissions paid via PayPal.</p>
                  <div className="flex gap-3 justify-center">
                    <Link to="/marketplace" className="px-6 py-3 bg-orange-500 text-white font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                      Browse Products
                    </Link>
                    <button onClick={() => setApplied(false)} className="px-6 py-3 border border-gray-200 text-gray-600 font-semibold rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
                      Apply Another
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
